import { Col, Row } from 'antd';
import React, { useState, useRef } from 'react'
import { FaUpload, FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import { RiCloseCircleFill } from 'react-icons/ri';
import { uploadImage, deleteImage } from '../stores/api_calls/upload';

const MAX_FILE_SIZE_MB = 10;

let slotKeySequence = 0;
const nextSlotKey = () => `slot-img-${++slotKeySequence}-${Date.now()}`;

/**
 * Reusable single ordered image list with immediate-upload semantics.
 * Slot shape: { key, _id, url, preview, uploading } — the array order IS the
 * display order sent to the API on submit.
 *
 * Images uploaded in this session are deleted on the server on remove;
 * images listed in `initialImageIds` (already attached to the record) are
 * removed locally only — the update endpoint unassigns them on submit.
 */
const SlotImageUploader = ({
    selectedFiles,
    setSelectedFiles,
    initialImageIds = [],
    maxImages = 9,
    watermark = false,
    directSeller = false,
}) => {

    const fileUploader = useRef(null)
    const [error, setError] = useState({ isError: false, message: "" })

    const enableUploader = selectedFiles.length < maxImages;

    const handleOpenGallery = () => {
        fileUploader.current.click();
    };

    const updateSlot = (key, patch) => {
        setSelectedFiles((previous) =>
            previous.map((slot) => (slot.key === key ? { ...slot, ...patch } : slot))
        );
    };

    const removeSlot = (key) => {
        setSelectedFiles((previous) => previous.filter((slot) => slot.key !== key));
    };

    const uploadSlot = async (slot, orderNumber) => {
        try {
            // Provisional 1-based order; the authoritative order is the
            // image-id array sent on submit.
            const res = await uploadImage(slot.file, orderNumber, watermark, directSeller);
            const data = res?.data?.data;

            if (data?._id) {
                updateSlot(slot.key, {
                    _id: data._id,
                    url: data?.metadata?.image_url ?? "",
                    uploading: false,
                    file: null,
                });
            } else {
                throw new Error("Upload failed");
            }
        } catch (err) {
            removeSlot(slot.key);
            setError({
                isError: true,
                message: err?.response?.data?.server_response ?? "Image upload failed. Please try again.",
            });
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files)

            if (selectedFiles.length + filesArray.length > maxImages) {
                setError({ isError: true, message: `You only need to upload ${maxImages} images.` })
                e.target.value = "";
                return;
            }

            const oversized = filesArray.find((file) => file.size / 1048576 > MAX_FILE_SIZE_MB);
            if (oversized) {
                setError({ isError: true, message: `"${oversized.name}" exceeds the maximum size of ${MAX_FILE_SIZE_MB}MB per image.` })
                e.target.value = "";
                return;
            }

            setError({ isError: false, message: "" })

            const newSlots = filesArray.map((file) => ({
                key: nextSlotKey(),
                _id: "",
                url: "",
                preview: URL.createObjectURL(file),
                uploading: true,
                file,
            }));

            setSelectedFiles([...selectedFiles, ...newSlots]);

            newSlots.forEach((slot, i) => {
                uploadSlot(slot, selectedFiles.length + i + 1);
            });

            e.target.value = "";
        }
    }

    const onRemoveImage = (slot) => {
        if (slot._id && !(initialImageIds ?? []).includes(slot._id)) {
            deleteImage(slot._id).catch(() => {
                setError({ isError: true, message: "Failed to remove the image from the server." });
            });
        }

        if (slot.preview) URL.revokeObjectURL(slot.preview);
        removeSlot(slot.key);
    }

    // Swap with the adjacent slot — array order is the display order.
    const onMoveImage = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= selectedFiles.length) return;

        setSelectedFiles((previous) => {
            const updated = [...previous];
            [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
            return updated;
        });
    };

    return (
        <div>
            {error.isError ? <p className='upload-error-text'>{error.message}</p> : ""}

            <Row>
                {enableUploader && (
                    <Col span={8}>
                        <div className='upload-container col-div' onClick={handleOpenGallery}>
                            <FaUpload/>
                            <p>UPLOAD MULTIPLE PHOTOS</p>
                        </div>

                        <input
                            ref={fileUploader}
                            type="file"
                            hidden
                            multiple
                            onChange={handleImageChange}
                            accept=".jpg, .png, .jpeg"
                        />
                    </Col>
                )}

                {selectedFiles.map((slot, i) =>
                    <Col span={8} key={slot.key}>
                        <div className='image-container col-div'>
                            {slot.uploading && (
                                <span style={{ position: "absolute", fontSize: 18, fontWeight: "bold", zIndex: 1 }}>
                                    Uploading...
                                </span>
                            )}
                            <div
                                className='removeIcon'
                                onClick={() => onRemoveImage(slot)}
                            >
                                <RiCloseCircleFill/>
                            </div>
                            <img
                                src={slot.url || slot.preview}
                                style={{ opacity: slot.uploading ? 0.4 : 1 }}
                            />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 16px 12px 16px',
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => onMoveImage(i, -1)}
                                disabled={i === 0 || slot.uploading}
                                style={{ cursor: i === 0 ? 'not-allowed' : 'pointer', padding: '4px 12px' }}
                                title="Move image left"
                            >
                                <FaArrowLeft/>
                            </button>
                            <span style={{ fontWeight: 'bold' }}>{i + 1}</span>
                            <button
                                type="button"
                                onClick={() => onMoveImage(i, 1)}
                                disabled={i === selectedFiles.length - 1 || slot.uploading}
                                style={{ cursor: i === selectedFiles.length - 1 ? 'not-allowed' : 'pointer', padding: '4px 12px' }}
                                title="Move image right"
                            >
                                <FaArrowRight/>
                            </button>
                        </div>
                    </Col>
                )}
            </Row>
        </div>
    )
}

export default SlotImageUploader
